/**
 * V2 admin · Add Property wizard (COL-529 V3).
 *
 * 4-step linear flow: Objekt → Wohnungen → Checkliste → Prüfung. State held in
 * React context (WizardContext); the property itself isn't created until the
 * final Prüfung submit. In between, a running draft of the whole wizard state
 * is autosaved to `property_drafts` (see usePropertyDraft) so an interrupted
 * add can be resumed — a follow-up wires the resume side.
 *
 * Step is read from `?step=N` so deep-links and "Edit from review" work.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { AdminV2Layout } from "@/components/admin/v2/AdminV2Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  INITIAL_WIZARD_STATE,
  WizardContext,
  type WizardState,
} from "@/components/admin/v2/wizard/wizardContext";
import { WizardStepper } from "@/components/admin/v2/wizard/WizardStepper";
import { useApartmentCountSync } from "@/components/admin/v2/wizard/useApartmentCountSync";
import { WIZARD_STEPS } from "@/components/admin/v2/wizard/wizardSteps";
import { Step1Property } from "@/components/admin/v2/wizard/Step1Property";
import { Step2Apartments } from "@/components/admin/v2/wizard/Step2Apartments";
import { Step3Checklist } from "@/components/admin/v2/wizard/Step3Checklist";
import { Step3Review } from "@/components/admin/v2/wizard/Step3Review";
import {
  usePropertyDraft,
  isWorthSaving,
  type DraftStatus,
} from "@/hooks/v2/usePropertyDraft";
import { useCreatePropertyV2 } from "@/hooks/v2/useCreatePropertyV2";
import { useKeyNestAvailability } from "@/hooks/v2/useKeyNestAvailability";
import {
  useResubmitProperty,
  useSubmissionForEdit,
  useSubmitPropertyForApproval,
} from "@/hooks/v2/usePropertySubmissions";
import { useAuth } from "@/contexts/AuthContext";
import { validateWizardStep } from "@/components/admin/v2/wizard/wizardValidation";
import { useLessors } from "@/hooks/v2/useLessors";
import { composeStreetAddress } from "@/lib/address";
import { useLanguage } from "@/contexts/LanguageContext";
/**
 * The property wizard, at /admin/properties/new — the staff path and the
 * owner's "Meine Objekte" → "Immobilie hinzufügen" both land here.
 */
export default function V2PropertyNew() {
  const { isOwnerPortalOnly } = useAuth();
  const { t } = useLanguage();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [state, setState] = useState<WizardState>(INITIAL_WIZARD_STATE);
  const [showAbandon, setShowAbandon] = useState(false);
  const createMut = useCreatePropertyV2();
  const submitMut = useSubmitPropertyForApproval();
  const resubmitMut = useResubmitProperty();

  /** Where "done" goes: back to the list. */
  const leaveWizard = useCallback(() => {
    navigate("/admin/properties");
  }, [navigate]);

  const editId = params.get("edit");
  const editPropertyId = editId ? Number(editId) : null;
  const editSubmission = useSubmissionForEdit(
    isOwnerPortalOnly ? editPropertyId : null
  );
  const [loadedEditId, setLoadedEditId] = useState<number | null>(null);
  useEffect(() => {
    if (
      editSubmission.data &&
      editPropertyId &&
      loadedEditId !== editPropertyId
    ) {
      setState(editSubmission.data);
      setLoadedEditId(editPropertyId);
    }
  }, [editSubmission.data, editPropertyId, loadedEditId]);

  const stepRaw = Number(params.get("step") ?? 1);
  const current =
    Number.isFinite(stepRaw) && stepRaw >= 1 && stepRaw <= 4 ? stepRaw : 1;

  // ── derived: how far the user has progressed ───────────────────────
  const furthestReached = useMemo(() => {
    // Step 2 (Wohnungen) needs the basic property fields; once past it,
    // Checkliste (3) and Prüfung (4) are freely reachable — neither gates
    // forward progress (the checklist is tracked, not enforced here).
    let f = 1;
    if (state.property.title && state.property.address && state.property.city)
      f = Math.max(f, 2);
    if (f >= 2) f = Math.max(f, 4);
    return Math.max(f, current);
  }, [state, current]);

  // ── ctx wiring ───────────────────────────────────────────────────────
  const ctxValue = useMemo(
    () => ({
      state,
      setProperty: (patch: Partial<WizardState["property"]>) =>
        setState((s) => {
          const property = { ...s.property, ...patch };
          // Keep the derived `address` column in sync whenever either
          // structured field changes, instead of relying on every caller to
          // compute it (see composeStreetAddress).
          if ("street_name" in patch || "street_number" in patch) {
            property.address = composeStreetAddress(
              property.street_name,
              property.street_number
            );
          }
          return { ...s, property };
        }),
      setApartments: (apartments: WizardState["apartments"]) =>
        setState((s) => ({ ...s, apartments })),
      setOwnerEmail: (patch: Partial<WizardState["ownerEmail"]>) =>
        setState((s) => ({ ...s, ownerEmail: { ...s.ownerEmail, ...patch } })),
      reset: () => setState(INITIAL_WIZARD_STATE),
    }),
    [state]
  );

  // Keeps Step 2's apartments in sync with Step 1's apartment count. Lives
  // here (not in Step2Apartments) because this page stays mounted for the
  // whole wizard, while Step2Apartments remounts on every visit (COL-379).
  useApartmentCountSync(state, ctxValue.setApartments);

  // COL-529: resolve KeyNest availability once at the wizard root from the
  // geocoded property coords, and mirror the verdict onto wizard state so the
  // Step 3 checklist + both submit paths read a single source of truth. The
  // equality guard keeps this from looping — setProperty spreads state, so an
  // unconditional write would re-render every render.
  const keynest = useKeyNestAvailability(
    state.property.lat,
    state.property.lng
  );
  useEffect(() => {
    if (state.property.keynestAvailable !== keynest.available) {
      ctxValue.setProperty({ keynestAvailable: keynest.available });
    }
  }, [keynest.available, state.property.keynestAvailable, ctxValue]);

  // Autosave + resume the running wizard state via property_drafts. Only for a
  // fresh add — an edit (?edit=<id>) is already hydrated from its submission, so
  // a parallel draft would just be a stale duplicate. The hook loads the user's
  // most-recent draft on mount (restored below); a draft-list UI is a follow-up.
  const draft = usePropertyDraft({
    state,
    currentStep: current,
    enabled: !editPropertyId,
  });

  // ── nav ──────────────────────────────────────────────────────────────
  function goTo(step: number) {
    setParams({ step: String(step) });
  }
  function next() {
    if (current < 4) goTo(current + 1);
  }
  function back() {
    if (current > 1) goTo(current - 1);
  }
  function abandon() {
    setShowAbandon(true);
  }
  // "Verwerfen": drop the in-progress draft and leave.
  function discardAndLeave() {
    setShowAbandon(false);
    draft.discard();
    leaveWizard();
  }
  // "Für später speichern": flush the draft, then leave so it can be resumed.
  function saveDraftAndLeave() {
    setShowAbandon(false);
    draft.saveNow();
    leaveWizard();
  }

  // Resume an interrupted add: once the draft lookup settles, restore the
  // user's most-recent draft into the wizard. Once only, and never over an
  // edit (hydrated from its submission) or input the user has already started.
  const resumedApplied = useRef(false);
  useEffect(() => {
    if (resumedApplied.current || editPropertyId || !draft.resumed) return;
    resumedApplied.current = true;
    if (isWorthSaving(state)) {
      // User already started typing — keep their input and drop the stale draft
      // we found (don't adopt its id, or autosave would overwrite it with this
      // unrelated input, and don't leave it, or it resurrects next visit).
      draft.discardResumed();
      return;
    }
    // Adopt the draft's id only now that we're actually restoring it, so
    // autosave upserts that row instead of overwriting an unrelated draft.
    draft.adoptResumed();
    setState(draft.resumed.state);
    goTo(draft.resumed.currentStep);
    // goTo is a hoisted declaration; state read here is the pristine-check only.
  }, [draft.resumed, editPropertyId, state]);

  // ── validation per step ──────────────────────────────────────────────
  // For the representor requirement: a linked lessorId alone doesn't
  // guarantee a representor (lessors.rep_name is nullable), so resolve the
  // actual name per lessor here rather than trusting the id's presence in
  // the pure validateWizardStep. Not needed (or fetched) for an owner
  // submitting their own building — the requirement doesn't apply there.
  const {
    data: lessorsForValidation,
    isLoading: lessorsForValidationLoading,
    isError: lessorsForValidationErrored,
    refetch: refetchLessorsForValidation,
  } = useLessors(undefined, !isOwnerPortalOnly);
  const lessorRepNames = useMemo(() => {
    if (!lessorsForValidation) return undefined;
    const map: Record<string, string> = {};
    for (const lessor of lessorsForValidation) {
      if (lessor.rep_name?.trim()) map[lessor.id] = lessor.rep_name.trim();
    }
    return map;
  }, [lessorsForValidation]);

  const stepValidation = useMemo(
    () =>
      validateWizardStep(state, current, t, {
        ownerSubmitting: isOwnerPortalOnly,
        lessorRepNames,
        // Grandfather pre-checklist submissions being edited (COL-529): the flag
        // rides along on the restored WizardState from useSubmissionForEdit.
        skipChecklistGate: !!state.checklistGrandfathered,
      }),
    [state, current, t, isOwnerPortalOnly, lessorRepNames]
  );
  // Until the lessor list resolves, lessorRepNames is undefined and
  // validateWizardStep falls back to trusting a linked lessorId — a staff
  // user could pick a lessor with a blank rep_name and advance/submit before
  // the query (or a retry after a failure) settles. Block Next/Submit on
  // steps 2+ for that window rather than let the trusting fallback apply
  // past it. Step 1 doesn't need this data, so it isn't gated.
  const lessorDataPending =
    !isOwnerPortalOnly &&
    current >= 2 &&
    (lessorsForValidationLoading || lessorsForValidationErrored);

  // ── submit ───────────────────────────────────────────────────────────
  function submit() {
    if (isOwnerPortalOnly) {
      const unknownError = t(
        "admin.v2.propertyNew.unknownError",
        "Unknown error"
      );
      const onError = (err: unknown) =>
        toast({
          title: t(
            "admin.v2.propertyNew.submitErrorTitle",
            "Could not submit property"
          ),
          description: localizeSubmitError(err, t) ?? unknownError,
          variant: "destructive",
        });
      if (editPropertyId) {
        resubmitMut.mutate(
          { propertyId: editPropertyId, state },
          {
            onSuccess: () => {
              toast({
                title: t(
                  "admin.v2.propertyNew.resubmittedTitle",
                  "Resubmitted"
                ),
                description: t(
                  "admin.v2.propertyNew.resubmittedDescription",
                  "coliviq will review your changes shortly."
                ),
              });
              leaveWizard();
            },
            onError,
          }
        );
      } else {
        submitMut.mutate(state, {
          onSuccess: () => {
            draft.discard();
            toast({
              title: t(
                "admin.v2.propertyNew.submittedTitle",
                "Property submitted"
              ),
              description: t(
                "admin.v2.propertyNew.submittedDescription",
                "Awaiting coliviq admin approval."
              ),
            });
            leaveWizard();
          },
          onError,
        });
      }
      return;
    }

    createMut.mutate(state, {
      onSuccess: (result) => {
        draft.discard();
        if (result.errors.length === 0) {
          toast({
            title: t("admin.v2.propertyNew.createdTitle", "Property created"),
            description: t(
              "admin.v2.propertyNew.createdDescription",
              "{count} {roomWord} added."
            )
              .replace("{count}", String(result.roomsCreated))
              .replace(
                "{roomWord}",
                result.roomsCreated === 1
                  ? t("admin.wizard.review.perRoomSuffix", "room")
                  : t("common.rooms", "rooms")
              ),
          });
        } else {
          toast({
            title: t(
              "admin.v2.propertyNew.createdWithIssuesTitle",
              "Property created with issues"
            ),
            description: t(
              "admin.v2.propertyNew.createdWithIssuesDescription",
              "{saved} rooms saved, {failed} failed."
            )
              .replace("{saved}", String(result.roomsCreated))
              .replace("{failed}", String(result.roomsFailed)),
            variant: "destructive",
          });
        }
        navigate(`/admin/properties/${result.propertyId}`);
      },
      onError: (err) =>
        toast({
          title: t(
            "admin.v2.propertyNew.createErrorTitle",
            "Could not create property"
          ),
          description:
            err instanceof Error
              ? err.message
              : t("admin.v2.propertyNew.unknownError", "Unknown error"),
          variant: "destructive",
        }),
    });
  }

  const isSubmitting = isOwnerPortalOnly
    ? submitMut.isPending || resubmitMut.isPending
    : createMut.isPending;

  // Reset internal step if URL has out-of-range step (page mode only — modal
  // mode keeps the step in local state, never the URL).
  useEffect(() => {
    if (stepRaw !== current) {
      setParams({ step: String(current) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentStepMeta = WIZARD_STEPS.find((s) => s.n === current);
  const stepTitle = currentStepMeta
    ? t(currentStepMeta.titleKey, currentStepMeta.title)
    : t("admin.v2.propertyNew.propertyFallback", "Property");

  // A stale/foreign ?edit=<id> (already reviewed, withdrawn, or not this
  // owner's) resolves server-side to no data — show that plainly instead of
  // silently falling back to a blank "new property" wizard.
  const editNotFound =
    isOwnerPortalOnly &&
    !!editPropertyId &&
    editSubmission.isSuccess &&
    editSubmission.data === null;

  if (editNotFound) {
    return (
      <AdminV2Layout
        title={t(
          "admin.v2.propertyNew.submissionNotFound",
          "Submission not found"
        )}
        breadcrumbs={[
          {
            label: t("admin.v2.propertyNew.myProperties", "My Properties"),
            to: "/admin/properties",
          },
          { label: t("admin.v2.propertyNew.notFound", "Not found") },
        ]}
      >
        <Card className="p-8 space-y-3 max-w-lg">
          <h2 className="text-lg font-semibold">
            {t(
              "admin.v2.propertyNew.submissionNotFound",
              "Submission not found"
            )}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t(
              "admin.v2.propertyNew.submissionNotFoundHint",
              "This submission may have already been reviewed, withdrawn, or doesn't belong to your account."
            )}
          </p>
          <Button asChild variant="outline" size="sm">
            <Link to="/admin/properties">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t(
                "admin.v2.propertyNew.backToMyProperties",
                "Back to My Properties"
              )}
            </Link>
          </Button>
        </Card>
      </AdminV2Layout>
    );
  }

  /**
   * The wizard itself. Hoisted so the page and the dialog render exactly
   * the same markup — the only difference between them is the chrome
   * around it.
   */
  const wizardBody = (
    <>
      <WizardStepper
        current={current}
        furthestReached={furthestReached}
        onJump={goTo}
      />

      <div className="space-y-4">
        {current === 1 && <Step1Property />}
        {current === 2 && <Step2Apartments />}
        {current === 3 && <Step3Checklist />}
        {current === 4 && <Step3Review onJump={goTo} />}
      </div>

      {/* Footer nav */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 sticky bottom-0 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-t pt-4">
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={abandon}>
            {t("admin.v2.propertyNew.cancel", "Cancel")}
          </Button>
          {draft.enabled && (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={draft.saveNow}
                disabled={draft.status === "saving"}
              >
                {draft.status === "saving" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                {t("admin.properties.saveDraft", "Save")}
              </Button>
              <DraftStatus status={draft.status} t={t} />
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={back}
            disabled={current === 1}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("admin.v2.propertyNew.back", "Back")}
          </Button>

          {current < 4 ? (
            <Button
              type="button"
              size="sm"
              onClick={next}
              disabled={!stepValidation.canAdvance || lessorDataPending}
            >
              {t("admin.v2.propertyNew.nextPrefix", "Next:")}{" "}
              {WIZARD_STEPS[current]
                ? t(WIZARD_STEPS[current].titleKey, WIZARD_STEPS[current].title)
                : t("admin.v2.propertyNew.continue", "Continue")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              onClick={submit}
              disabled={
                isSubmitting || !stepValidation.canAdvance || lessorDataPending
              }
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isOwnerPortalOnly
                    ? t("admin.v2.propertyNew.submitting", "Submitting…")
                    : t("admin.v2.propertyNew.creating", "Creating…")}
                </>
              ) : isOwnerPortalOnly ? (
                editPropertyId ? (
                  t(
                    "admin.v2.propertyNew.resubmitForApproval",
                    "Resubmit for approval"
                  )
                ) : (
                  t(
                    "admin.v2.propertyNew.submitForApproval",
                    "Submit for approval"
                  )
                )
              ) : (
                t(
                  "admin.v2.propertyNew.createPropertyWithApartments",
                  `Create property${
                    state.apartments.length
                      ? ` + ${state.apartments.length} ${
                          state.apartments.length === 1
                            ? "apartment"
                            : "apartments"
                        }`
                      : ""
                  }`
                )
              )}
            </Button>
          )}
        </div>
      </div>

      {lessorDataPending ? (
        <div className="mt-2 flex items-center justify-end gap-2 text-xs text-destructive">
          {lessorsForValidationErrored ? (
            <>
              <span>
                {t(
                  "admin.v2.propertyNew.couldNotLoadOwnerData",
                  "Could not load owner data — representors can't be checked."
                )}
              </span>
              <button
                type="button"
                className="underline"
                onClick={() => refetchLessorsForValidation()}
              >
                {t("common.action.tryAgain", "Try again")}
              </button>
            </>
          ) : (
            t(
              "admin.v2.propertyNew.loadingOwnerDataForValidation",
              "Loading owner data to check representors…"
            )
          )}
        </div>
      ) : !stepValidation.canAdvance && stepValidation.message ? (
        <div className="mt-2 text-xs text-muted-foreground text-right">
          {translateValidationMessage(stepValidation.message, t)}
          {typeof stepValidation.openCount === "number" &&
            stepValidation.openCount > 0 && (
              <>
                {" — "}
                <span className="font-medium text-destructive">
                  {t(
                    "admin.checklist.ownerGateStillOpen",
                    "{n} still open"
                  ).replace("{n}", String(stepValidation.openCount))}
                </span>
              </>
            )}
        </div>
      ) : (
        // Non-blocking advisory (admin smoke-detector warning) — shown while
        // advancing is still allowed, so it can't ride on `message`.
        stepValidation.info && (
          <div className="mt-2 text-xs text-destructive text-right">
            {translateValidationMessage(stepValidation.info, t)}
          </div>
        )
      )}

      {/* Abandon confirmation */}
      <AlertDialog open={showAbandon} onOpenChange={setShowAbandon}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                "admin.v2.propertyNew.discardAndExit",
                `Discard ${stepTitle.toLowerCase()} and exit?`
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {draft.enabled
                ? t(
                    "admin.properties.discardHintDraft",
                    "You can save your progress as a draft and finish later, or discard everything you've entered."
                  )
                : t(
                    "admin.v2.propertyNew.discardHint",
                    "Nothing has been saved yet. Closing the wizard now will lose all the fields you've entered."
                  )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("admin.v2.propertyNew.keepEditing", "Keep editing")}
            </AlertDialogCancel>
            {draft.enabled && (
              <Button
                type="button"
                variant="outline"
                onClick={saveDraftAndLeave}
              >
                {t("admin.properties.saveForLater", "Save for later")}
              </Button>
            )}
            <AlertDialogAction onClick={discardAndLeave}>
              {t("admin.v2.propertyNew.discard", "Discard")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  return (
    <WizardContext.Provider value={ctxValue}>
      <AdminV2Layout
        title={
          isOwnerPortalOnly
            ? editPropertyId
              ? t("admin.v2.propertyNew.editProperty", "Edit property")
              : t("admin.v2.propertyNew.addAProperty", "Add a property")
            : t("admin.v2.propertyNew.addProperty", "Add property")
        }
        breadcrumbs={
          isOwnerPortalOnly
            ? [
                {
                  label: t(
                    "admin.v2.propertyNew.myProperties",
                    "My Properties"
                  ),
                  to: "/admin/properties",
                },
                {
                  label: editPropertyId
                    ? t("admin.v2.propertyNew.edit", "Edit")
                    : t("admin.v2.propertyNew.new", "New"),
                },
              ]
            : [
                {
                  label: t("admin.v2.propertyNew.admin", "Admin"),
                  to: "/admin",
                },
                {
                  label: t("admin.v2.propertyNew.properties", "Properties"),
                  to: "/admin/properties",
                },
                { label: t("admin.v2.propertyNew.new", "New") },
              ]
        }
      >
        {wizardBody}
      </AdminV2Layout>
    </WizardContext.Provider>
  );
}

/**
 * validateWizardStep (wizardValidation.ts) is pure/untranslated so its rules
 * stay unit-testable against fixed English text — this maps its handful of
 * static messages to a translation, and passes anything else (the dynamic
 * street-name format error) through as-is.
 */
/**
 * Map the owner-submit RPCs' English `RAISE EXCEPTION` text to a localized
 * toast. The client gate normally stops these cases before submit, so a raw
 * Postgres string reaching a German owner is a backstop path — match on the
 * stable substring (the apartment label is interpolated into the checklist
 * one, so match its prefix). Returns null for anything unrecognized so the
 * caller falls back to its generic message rather than leaking raw SQL.
 */
function localizeSubmitError(
  err: unknown,
  t: (key: string, fallback?: string) => string
): string | null {
  const msg = err instanceof Error ? err.message : "";
  if (msg.includes("Go-live checklist incomplete for apartment")) {
    return t(
      "admin.v2.propertyNew.submitErrorChecklistIncomplete",
      "The go-live checklist isn't complete yet — every apartment needs all 15 items fulfilled (smoke detector confirmed) before submitting."
    );
  }
  if (msg.includes("At least one apartment is required before submitting")) {
    return t(
      "admin.v2.propertyNew.submitErrorNoApartments",
      "Add at least one apartment before submitting."
    );
  }
  return null;
}

function translateValidationMessage(
  message: string,
  t: (key: string, fallback?: string) => string
): string {
  switch (message) {
    case "Title, street name, street number, postal code, and city are required.":
      return t(
        "admin.properties.requiredFieldsStep1",
        "Title, street name, street number, postal code, and city are required."
      );
    case "Add at least one apartment.":
      return t(
        "admin.properties.addAtLeastOneApartment",
        "Add at least one apartment."
      );
    case "Every apartment needs a label.":
      return t(
        "admin.properties.everyApartmentNeedsLabel",
        "Every apartment needs a label."
      );
    case "Every room needs a name.":
      return t(
        "admin.properties.everyRoomNeedsName",
        "Every room needs a name."
      );
    case "Every apartment needs a representor.":
      return t(
        "admin.properties.everyApartmentNeedsRepresentor",
        "Every apartment needs a representor."
      );
    case "Some required fields are missing — use the Edit links above.":
      return t(
        "admin.properties.requiredFieldsMissing",
        "Some required fields are missing — use the Edit links above."
      );
    case "Every apartment must complete all 15 checklist proofs before you can continue.":
      return t(
        "admin.checklist.ownerGateIncomplete",
        "Every apartment must complete all 15 checklist proofs before you can continue."
      );
    case 'Every apartment needs a smoke detector — resolve the "no" answer before continuing.':
      return t(
        "admin.checklist.ownerGateSmoke",
        'Every apartment needs a smoke detector — resolve the "no" answer before continuing.'
      );
    case 'At least one apartment has its smoke-detector check marked "no" — go-live is blocked until that\'s resolved.':
      return t(
        "admin.checklist.adminSmokeWarning",
        'At least one apartment has its smoke-detector check marked "no" — go-live is blocked until that\'s resolved.'
      );
    default:
      return message;
  }
}

/** Tiny autosave indicator next to the Save button in the footer. */
function DraftStatus({
  status,
  t,
}: {
  status: DraftStatus;
  t: (key: string, fallback?: string) => string;
}) {
  if (status === "saving") {
    return (
      <span className="text-xs text-muted-foreground">
        {t("admin.properties.draftSaving", "Saving…")}
      </span>
    );
  }
  if (status === "saved") {
    return (
      <span className="text-xs text-muted-foreground">
        {t("admin.properties.draftSaved", "Draft saved")}
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className="text-xs text-destructive">
        {t("admin.properties.draftSaveFailed", "Couldn't save draft")}
      </span>
    );
  }
  return null;
}
